import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  IChangeLocationStatusPayload,
  ICreateLocationNodePayload,
  ILocationRegistrySlice,
  ISaveLocationGeometryPayload,
} from 'types/locationRegistry.interface'
import {
  ICreateLocationNodeRequest,
  ILocationTreeNodeResponse,
  ISaveNodeGeometryRequest,
  IUpdateLocationNodeRequest,
  IUpdateNodeStatusRequest,
} from 'types/api/locationRegistryApi.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import {
  LOCATION_TYPE_TO_API,
  HOLD_CATEGORY_TO_API,
  STATUS_TO_API,
  mapApiNodeToUi,
  mapFlatNodes,
  mapTreeResponse,
  ptsToGeoJson,
} from './locationRegistryMappers'
import { ILocationGeos } from 'types/locationsRegistry.interface'

const initialState: ILocationRegistrySlice = {
  isLoading: false,
  tree: null,
  geos: {},
  unmapped: [],
  roads: [],
}

const refreshRegistry = async (dispatch: any) => {
  await Promise.all([
    dispatch(getLocationRegistryTree()),
    dispatch(getUnmappedLocations()),
    dispatch(getLocationRoads()),
  ])
}

export const getLocationRegistryTree = createAsyncThunk(
  'locationRegistry/getLocationRegistryTree',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<ILocationTreeNodeResponse[]> = await baseUrl.get('locations/tree')
      return mapTreeResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getUnmappedLocations = createAsyncThunk(
  'locationRegistry/getUnmappedLocations',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<ILocationTreeNodeResponse[]> = await baseUrl.get('locations/unmapped')
      return mapFlatNodes(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getLocationRoads = createAsyncThunk(
  'locationRegistry/getLocationRoads',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<ILocationTreeNodeResponse[]> = await baseUrl.get('locations/roads')
      return mapFlatNodes(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createLocationNode = createAsyncThunk(
  'locationRegistry/createLocationNode',
  async (payload: ICreateLocationNodePayload, thunkAPI) => {
    const req: ICreateLocationNodeRequest = {
      parentId: payload.parentId,
      type: LOCATION_TYPE_TO_API[payload.type],
      name: payload.name,
      attrs: payload.attrs !== undefined ? payload.attrs : null,
      restricted: payload.restricted,
    }
    try {
      const resp: AxiosResponse<ILocationTreeNodeResponse> = await baseUrl.post('locations', req, thunkAPI)
      await refreshRegistry(thunkAPI.dispatch)
      const geos: ILocationGeos = {}
      return mapApiNodeToUi(resp.data, geos)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateLocationNode = createAsyncThunk(
  'locationRegistry/updateLocationNode',
  async ({ id, ...payload }: IUpdateLocationNodeRequest & { id: number }, thunkAPI) => {
    const req: IUpdateLocationNodeRequest = {
      ...payload,
      attrs: payload.attrs === undefined ? undefined : null,
    }
    try {
      const resp: AxiosResponse<ILocationTreeNodeResponse> = await baseUrl.patch(
        `locations/${id}`,
        req,
        thunkAPI,
      )
      await refreshRegistry(thunkAPI.dispatch)
      const geos: ILocationGeos = {}
      return mapApiNodeToUi(resp.data, geos)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const saveLocationGeometry = createAsyncThunk(
  'locationRegistry/saveLocationGeometry',
  async (payload: ISaveLocationGeometryPayload, thunkAPI) => {
    const req: ISaveNodeGeometryRequest = {
      geojson: ptsToGeoJson(payload.draft, payload.nodeType),
      source: 1,
    }
    try {
      await baseUrl.put(`locations/${payload.id}/geometry`, req, thunkAPI)
      await refreshRegistry(thunkAPI.dispatch)
      return payload.id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const changeLocationStatus = createAsyncThunk(
  'locationRegistry/changeLocationStatus',
  async (payload: IChangeLocationStatusPayload, thunkAPI) => {
    const req: IUpdateNodeStatusRequest = {
      status: STATUS_TO_API[payload.status],
      reason: payload.reason,
      category: payload.category ? HOLD_CATEGORY_TO_API[payload.category] : undefined,
    }
    try {
      await baseUrl.patch(`locations/${payload.id}/status`, req, thunkAPI)
      await refreshRegistry(thunkAPI.dispatch)
      return payload.id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const locationRegistrySlice = createSlice({
  name: 'locationRegistry',
  initialState,
  reducers: {
    clearLocationRegistry: (state) => {
      state.tree = null
      state.geos = {}
      state.unmapped = []
      state.roads = []
    },
  },
  extraReducers: {
    [getLocationRegistryTree.pending.type]: (state) => {
      state.isLoading = true
    },
    [getLocationRegistryTree.fulfilled.type]: (
      state,
      { payload }: { payload: ReturnType<typeof mapTreeResponse> },
    ) => {
      state.isLoading = false
      state.tree = payload.tree
      state.geos = payload.geos
    },
    [getLocationRegistryTree.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getUnmappedLocations.fulfilled.type]: (
      state,
      { payload }: { payload: ReturnType<typeof mapFlatNodes> },
    ) => {
      state.unmapped = payload
    },
    [getUnmappedLocations.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getLocationRoads.fulfilled.type]: (
      state,
      { payload }: { payload: ReturnType<typeof mapFlatNodes> },
    ) => {
      state.roads = payload
    },
    [getLocationRoads.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [createLocationNode.pending.type]: (state) => {
      state.isLoading = true
    },
    [createLocationNode.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Location created successfully')
    },
    [createLocationNode.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [updateLocationNode.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateLocationNode.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Location updated successfully')
    },
    [updateLocationNode.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [saveLocationGeometry.pending.type]: (state) => {
      state.isLoading = true
    },
    [saveLocationGeometry.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Geometry saved successfully')
    },
    [saveLocationGeometry.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [changeLocationStatus.pending.type]: (state) => {
      state.isLoading = true
    },
    [changeLocationStatus.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Status updated successfully')
    },
    [changeLocationStatus.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearLocationRegistry } = locationRegistrySlice.actions
export default locationRegistrySlice.reducer
